import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ArrowLeft, Plus, Trash2, Save, Send, Sparkles, Pause, Play, Trash } from "lucide-react";
import { TierBadge, TIER_META } from "@/components/Bits";

export default function CampaignDetail() {
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [cr, er] = await Promise.all([
        api.get(`/campaigns/${campaignId}`),
        api.get(`/campaigns/${campaignId}/enrollments`),
      ]);
      setC(cr.data);
      setEnrollments(er.data.items);
    } catch {
      toast.error("Campaign not found");
      navigate("/campaigns");
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [campaignId]);

  const updateStep = (idx, patch) => {
    const steps = [...c.steps];
    steps[idx] = { ...steps[idx], ...patch };
    setC({ ...c, steps });
  };
  const addStep = () => {
    const steps = [...(c.steps || [])];
    steps.push({
      order: steps.length,
      delay_days: steps.length === 0 ? 0 : 4,
      subject: "Following up on visibility on paisley.org.uk",
      body_html: "<p>Hi {{contact_name}},</p><p>...</p>",
    });
    setC({ ...c, steps });
  };
  const delStep = (idx) => {
    const steps = c.steps.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i }));
    setC({ ...c, steps });
  };
  const saveAll = async () => {
    setSaving(true);
    try {
      const payload = {
        name: c.name, description: c.description,
        target_tier: c.target_tier, target_categories: c.target_categories || [],
        steps: c.steps, active: c.active, require_approval: c.require_approval,
      };
      await api.patch(`/campaigns/${campaignId}`, payload);
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const del = async () => {
    if (!window.confirm("Delete this campaign?")) return;
    await api.delete(`/campaigns/${campaignId}`);
    toast.success("Deleted");
    navigate("/campaigns");
  };

  const sendNow = async (enrollmentId, useAi = false) => {
    try {
      const r = await api.post(`/campaigns/${campaignId}/enrollments/${enrollmentId}/send-now?use_ai_intro=${useAi ? "true" : "false"}`);
      if (r.data.ok) toast.success("Sent");
      else toast.error(r.data.error || r.data.reason || "Failed");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Send failed"); }
  };
  const pause = async (id) => { await api.post(`/campaigns/${campaignId}/enrollments/${id}/pause`); load(); };
  const resume = async (id) => { await api.post(`/campaigns/${campaignId}/enrollments/${id}/resume`); load(); };

  if (loading || !c) return <div className="text-sm text-gray-500">Loading…</div>;

  return (
    <div data-testid="campaign-detail-page">
      <Link to="/campaigns" className="inline-flex items-center text-xs text-gray-500 hover:text-[#1B4332] mb-4" data-testid="back-to-campaigns">
        <ArrowLeft className="h-3 w-3 mr-1" /> Back to campaigns
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div className="max-w-xl">
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-4xl tracking-tight">{c.name}</h1>
            <TierBadge tier={c.target_tier} />
          </div>
          <p className="text-sm text-gray-600 mt-2">{c.description}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="rounded-sm text-red-700 border-red-300 hover:bg-red-50" onClick={del} data-testid="delete-campaign-button">
            <Trash2 className="h-4 w-4" />
          </Button>
          <Button onClick={saveAll} disabled={saving} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm" data-testid="save-campaign-button">
            <Save className="h-4 w-4 mr-1.5" /> {saving ? "Saving…" : "Save"}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: sequence editor */}
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="campaign-settings-card">
            <div className="uppercase-tiny text-gray-500 mb-3">Campaign settings</div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} data-testid="campaign-name-input" /></div>
              <div><Label>Target tier</Label>
                <Select value={c.target_tier || "any"} onValueChange={(v) => setC({ ...c, target_tier: v === "any" ? null : v })}>
                  <SelectTrigger data-testid="campaign-tier-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="any">Any tier</SelectItem>
                    {Object.entries(TIER_META).map(([k, v]) => <SelectItem key={k} value={k}>{v.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-3"><Label>Description</Label><Input value={c.description || ""} onChange={(e) => setC({ ...c, description: e.target.value })} data-testid="campaign-description-input" /></div>
            <div className="mt-3 flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!c.active} onChange={(e) => setC({ ...c, active: e.target.checked })} data-testid="campaign-active-toggle" />
                <span>Active</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={!!c.require_approval} onChange={(e) => setC({ ...c, require_approval: e.target.checked })} data-testid="campaign-approval-toggle" />
                <span>Require lead approval before send</span>
              </label>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="campaign-steps-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="uppercase-tiny text-gray-500">Sequence steps</div>
                <div className="font-heading text-xl">{c.steps?.length || 0} steps</div>
              </div>
              <Button variant="outline" className="rounded-sm" onClick={addStep} data-testid="add-step-button">
                <Plus className="h-4 w-4 mr-1.5" /> Add step
              </Button>
            </div>
            <div className="space-y-4">
              {(c.steps || []).map((s, i) => (
                <div key={s.step_id || i} className="border border-gray-200 rounded-sm p-4" data-testid={`step-${i}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="uppercase-tiny">Step {i + 1}</div>
                    <button className="text-gray-400 hover:text-red-600" onClick={() => delStep(i)} data-testid={`delete-step-${i}`}>
                      <Trash className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-4 gap-3 mb-3">
                    <div className="col-span-1">
                      <Label>Delay (days)</Label>
                      <Input type="number" min="0" value={s.delay_days ?? 0} onChange={(e) => updateStep(i, { delay_days: parseInt(e.target.value || "0", 10) })} data-testid={`step-delay-${i}`} />
                    </div>
                    <div className="col-span-3">
                      <Label>Subject</Label>
                      <Input value={s.subject || ""} onChange={(e) => updateStep(i, { subject: e.target.value })} data-testid={`step-subject-${i}`} />
                    </div>
                  </div>
                  <Label>Body (HTML). Use {"{{business_name}}, {{contact_name}}, {{ai_intro}}"}</Label>
                  <Textarea rows={8} className="font-mono text-xs" value={s.body_html || ""} onChange={(e) => updateStep(i, { body_html: e.target.value })} data-testid={`step-body-${i}`} />
                </div>
              ))}
              {(c.steps || []).length === 0 && (
                <div className="text-center py-8 border border-dashed border-gray-300 rounded-sm text-sm text-gray-500">
                  No steps yet. Add one to start sending.
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right: enrollments */}
        <div>
          <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="campaign-enrollments-card">
            <div className="uppercase-tiny text-gray-500 mb-1">Enrollments</div>
            <div className="font-heading text-xl mb-4">{enrollments.length} leads</div>
            {enrollments.length === 0 && (
              <div className="text-center py-8 border border-dashed border-gray-300 rounded-sm text-xs text-gray-500">
                No leads enrolled yet. Open a lead to enroll.
              </div>
            )}
            <div className="space-y-2 max-h-[70vh] overflow-auto">
              {enrollments.map((e) => (
                <div key={e.enrollment_id} className="border border-gray-200 rounded-sm p-3" data-testid={`enrollment-${e.enrollment_id}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <Link to={`/leads/${e.lead_id}`} className="text-sm font-medium hover:text-[#1B4332] truncate block">
                        {e.business_name || e.lead_id}
                      </Link>
                      <div className="text-xs text-gray-500 truncate">{e.contact_email}</div>
                      <div className="text-[10px] uppercase-tiny mt-1 text-gray-500">
                        Step {e.current_step_index + 1} · {e.status}
                      </div>
                    </div>
                    <TierBadge tier={e.suggested_tier} />
                  </div>
                  <div className="mt-3 flex gap-1 flex-wrap">
                    {e.status === "active" ? (
                      <>
                        <Button size="sm" className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white text-xs h-7 px-2"
                          onClick={() => sendNow(e.enrollment_id, false)} data-testid={`send-now-${e.enrollment_id}`}>
                          <Send className="h-3 w-3 mr-1" /> Send
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-sm text-xs h-7 px-2"
                          onClick={() => sendNow(e.enrollment_id, true)} data-testid={`send-ai-${e.enrollment_id}`}>
                          <Sparkles className="h-3 w-3 mr-1" /> AI Send
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-sm text-xs h-7 px-2"
                          onClick={() => pause(e.enrollment_id)} data-testid={`pause-${e.enrollment_id}`}>
                          <Pause className="h-3 w-3" />
                        </Button>
                      </>
                    ) : e.status === "paused" ? (
                      <Button size="sm" variant="outline" className="rounded-sm text-xs h-7 px-2"
                        onClick={() => resume(e.enrollment_id)} data-testid={`resume-${e.enrollment_id}`}>
                        <Play className="h-3 w-3 mr-1" /> Resume
                      </Button>
                    ) : null}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
